import React, { useCallback, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Flex,
  Input,
  InputNumber,
  Popconfirm,
  Select,
  Space,
  Typography,
} from "antd";
import { CloseOutlined } from "@ant-design/icons";
import type { MessageInstance } from "antd/es/message/interface";
import { GPS, TrapSite, geocodeCandidate } from "../../../types";
import {
  useCreateTrapSiteMutation,
  useDeleteTrapSiteMutation,
  useLazyGeocodeReverseQuery,
  useLazyGeocodeSearchQuery,
  useUpdateTrapSiteMutation,
} from "../../../actions/trapsiteInQuery";
import { useUpdateDeviceMutation } from "../../../actions/devicesInQuery";
import { useTrapsiteEditorContext } from "./TrapsiteEditorContext";
import AddressField from "./AddressField";
import TrapsiteDeviceList from "./TrapsiteDeviceList";
import { EditorDevice, TrapsiteDraft } from "./types";
import {
  buildCreatePayload,
  buildUpdatePayload,
  devicesChanged as devicesDiffer,
  isDirty as draftIsDirty,
} from "./buildPayload";
import { diffDeviceAssignments, isEmptyDiff } from "./deviceDiff";
import {
  GEOCODE_CANDIDATE_LIMIT,
  PANEL_Z_INDEX,
  POPUP_Z_INDEX,
} from "./constants";
import { errorMessage } from "./errorMessage";
import { hasStagedMoves, stagedDeviceEntries } from "./stagedMoves";
import { nearbyPlacement, radiusOf } from "./trapProximity";

import {
  DISTANCE_UNITS,
  DistanceUnit,
  convertDistance,
  distanceToMeters,
  formatDistance,
  haversineMeters,
  unitAllowsDecimals,
} from "../../../utils/geo";

const { Title, Text } = Typography;
const { TextArea } = Input;

interface Props {
  /** The draft as it was when the panel opened, for dirty tracking. */
  initial: TrapsiteDraft;
  devices: EditorDevice[];
  /** Every collection location, to check this one's coverage against. */
  trapSites: Partial<TrapSite>[];
  container: HTMLElement | null;
  /**
   * Scoped message API. The global `message` renders at document.body, which
   * pseudo-fullscreen (z-index 99999 on the wrapper) hides completely - so
   * saving appeared to confirm nothing. This one renders inside the wrapper.
   */
  messageApi: MessageInstance;
  /** Brings a point into view, clear of the panel. Owned by the map layer. */
  onFramePoint: (gps: GPS) => void;
}

const TrapsiteEditorPanel: React.FC<Props> = ({
  initial,
  devices,
  trapSites,
  container,
  messageApi,
  onFramePoint,
}) => {
  const editor = useTrapsiteEditorContext();
  const draft = editor.draft!;
  const isEdit = editor.mode === "edit";

  const [candidates, setCandidates] = useState<geocodeCandidate[]>([]);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [lookupError, setLookupError] = useState<string | null>(null);
  const [noMatch, setNoMatch] = useState(false);

  const [runSearch, { isFetching: searching }] = useLazyGeocodeSearchQuery();
  const [runReverse, { isFetching: reverseLoading }] =
    useLazyGeocodeReverseQuery();

  const [createTrapSite, { isLoading: creating }] = useCreateTrapSiteMutation();
  const [updateTrapSite, { isLoading: updating }] = useUpdateTrapSiteMutation();
  const [deleteTrapSite, { isLoading: deleting }] = useDeleteTrapSiteMutation();

  const saving = creating || updating;

  const [updateDevice] = useUpdateDeviceMutation();

  // Dragging one of this location's traps around is an edit to this form, even
  // though the field it changes is not on it.
  const dirty = draftIsDirty(draft, initial) || hasStagedMoves(editor.staged);
  const deviceListChanged = devicesDiffer(draft, initial);

  const diff = useMemo(
    () =>
      diffDeviceAssignments(
        initial.deviceIds,
        draft.deviceIds,
        devices,
        editor.editingId,
      ),
    [initial.deviceIds, draft.deviceIds, devices, editor.editingId],
  );

  const handleFind = useCallback(
    async (override?: string) => {
      const q = (override ?? draft.address).trim();
      if (!q) return;

      setLookupError(null);
      setNoMatch(false);
      try {
        const result = await runSearch({
          q,
          limit: GEOCODE_CANDIDATE_LIMIT,
        }).unwrap();
        const items = result?.items ?? [];

        if (items.length === 0) {
          setCandidates([]);
          editor.patchDraft({ geocodedGps: null });
          setNoMatch(true);
          return;
        }

        if (items.length === 1) {
          // Unambiguous: apply it, normalising the typed text to the
          // geocoder's own label. The pin does not move.
          setCandidates([]);
          editor.patchDraft({
            address: items[0].address,
            details: items[0].details,
            geocodedGps: items[0].gps,
          });
          return;
        }

        setCandidates(items);
      } catch (err) {
        // Without this the rejection is unhandled, which in development shows
        // up as a full-screen overlay rather than a message in the form.
        setCandidates([]);
        setLookupError(
          errorMessage(err, "Could not look up that address. Try again."),
        );
      }
    },
    [draft.address, runSearch, editor],
  );

  const handleUpdateFromPin = useCallback(async () => {
    setLookupError(null);
    setNoMatch(false);
    try {
      const result = await runReverse(draft.gps).unwrap();
      if (!result?.item) {
        // A pin in open water or unaddressed land resolves to nothing. Say so
        // in the form rather than silently doing nothing, which is what this
        // used to do.
        setLookupError("No address found at the pin. Try moving it.");
        return;
      }
      setCandidates([]);
      editor.patchDraft({
        address: result.item.address,
        details: result.item.details,
        // The address now describes the pin exactly, so there is no drift.
        geocodedGps: result.item.gps,
      });
    } catch (err) {
      setLookupError(
        errorMessage(err, "Could not look up the address at the pin."),
      );
    }
  }, [draft.gps, runReverse, editor]);

  const handlePickCandidate = useCallback(
    (candidate: geocodeCandidate) => {
      setCandidates([]);
      setNoMatch(false);
      editor.patchDraft({
        address: candidate.address,
        details: candidate.details,
        geocodedGps: candidate.gps,
      });
    },
    [editor],
  );

  const handleSnapPin = useCallback(
    (gps: GPS) => {
      editor.movePinTo(gps);
      // A geocoded address can be a long way from where the pin was dropped,
      // so follow it - otherwise the pin silently leaves the viewport and the
      // snap looks like it did nothing.
      onFramePoint(gps);
    },
    [editor, onFramePoint],
  );

  /**
   * Add or remove traps on this location.
   *
   * A trap with no recorded position of its own gets one staged beside the
   * location, so it appears as a pin that can be dragged to where it actually
   * is. Without that it would be invisible on the map and unplaceable.
   */
  const handleDevicesChange = useCallback(
    (deviceIds: string[]) => {
      const byUuid = new Map(devices.map((d) => [d.uuid, d]));
      const added = deviceIds.filter((id) => !draft.deviceIds.includes(id));

      added.forEach((uuid, i) => {
        const device = byUuid.get(uuid);
        if (device && !device.gps && !editor.staged.devices[uuid]) {
          editor.stageDeviceMove(uuid, nearbyPlacement(draft.gps, i));
        }
      });

      editor.patchDraft({ deviceIds });
    },
    [devices, draft.deviceIds, draft.gps, editor],
  );

  const submit = useCallback(async () => {
    setSaveError(null);
    try {
      if (isEdit) {
        await updateTrapSite({
          id: editor.editingId!,
          ...buildUpdatePayload(draft, initial),
        }).unwrap();
      } else {
        await createTrapSite(buildCreatePayload(draft)).unwrap();
      }
      // Positions of this location's traps, dragged while the panel was open.
      // Staged rather than written on drop, so nothing changes until Save.
      for (const [uuid, gps] of stagedDeviceEntries(editor.staged)) {
        await updateDevice({ deviceUuid: uuid, gps }).unwrap();
      }

      messageApi.success("Collection location saved");
      // Close before the list refetch lands, so the draft pin and the real
      // marker never appear together.
      editor.close();
    } catch (err) {
      // The slice already raises a toast; this is the in-panel copy, which is
      // what a user reading the form actually sees.
      setSaveError(errorMessage(err, "Could not save this location."));
    }
  }, [
    isEdit,
    updateTrapSite,
    createTrapSite,
    draft,
    initial,
    editor,
    messageApi,
    updateDevice,
  ]);

  const handleDelete = useCallback(async () => {
    setSaveError(null);
    try {
      await deleteTrapSite(editor.editingId!).unwrap();
      messageApi.success("Collection location deleted");
      editor.close();
    } catch (err) {
      setSaveError(errorMessage(err, "Could not delete this location."));
    }
  }, [deleteTrapSite, editor, messageApi]);

  const deviceSummary = (
    <div className="flex flex-col gap-1 max-w-72">
      {diff.moved.map((d) => (
        <Text key={d.uuid}>
          Add {d.displayName} (moving from {d.fromTrapSiteName})
        </Text>
      ))}
      {diff.added.map((d) => (
        <Text key={d.uuid}>Add {d.displayName}</Text>
      ))}
      {diff.removed.map((d) => (
        <Text key={d.uuid}>Remove {d.displayName} (becomes Unassigned)</Text>
      ))}
    </div>
  );

  const canSave = dirty && draft.displayName.trim().length > 0;

  /** The radius in metres, for anything that has to compare distances. */
  const radiusMeters = distanceToMeters(draft.radiusValue, draft.radiusUnit);

  /**
   * Locations whose coverage this one runs into.
   *
   * Said in the panel as well as drawn on the map: the editor opens zoomed in
   * on the pin, so an overlap with a neighbour a kilometre away can easily be
   * off-screen entirely.
   *
   * `clearAt` is the radius that would just stop touching that neighbour.
   * Non-positive means this pin sits inside the neighbour's own circle, where
   * no radius clears it and one of them has to move.
   */
  const overlapping = useMemo(() => {
    const editingId = editor.editingId;
    return trapSites
      .filter((site) => site._id && site._id !== editingId && site.gps)
      .map((site) => {
        const gps = editor.staged.trapSites[site._id!] ?? site.gps!;
        const distance = haversineMeters(draft.gps, gps);
        const otherRadius = radiusOf(site);
        return {
          id: site._id!,
          name: site.display_name || "Unnamed location",
          distance,
          otherRadius,
          clearAt: distance - otherRadius,
        };
      })
      .filter((site) => site.distance < radiusMeters + site.otherRadius)
      .sort((a, b) => a.distance - b.distance);
  }, [trapSites, draft.gps, radiusMeters, editor.editingId, editor.staged]);

  /**
   * Traps on this location that sit further from it than they should.
   *
   * The same check the trap editor makes, from the other side: adding a trap
   * here is exactly when a mismatch is worth pointing out, and it recomputes
   * as either pin is dragged.
   */
  const farTraps = useMemo(() => {
    const byUuid = new Map(devices.map((d) => [d.uuid, d]));
    return draft.deviceIds
      .map((uuid) => {
        const device = byUuid.get(uuid);
        const gps = editor.staged.devices[uuid] ?? device?.gps;
        if (!device || !gps) return null;
        const distance = haversineMeters(gps, draft.gps);
        return distance > radiusMeters
          ? { uuid, name: device.displayName, distance }
          : null;
      })
      .filter(
        (x): x is { uuid: string; name: string; distance: number } => !!x,
      );
  }, [draft.deviceIds, draft.gps, radiusMeters, devices, editor.staged]);

  return (
    <div className="trapsite-editor-panel" style={{ zIndex: PANEL_Z_INDEX }}>
      <Flex
        justify="space-between"
        align="center"
        gap="small"
        className="trapsite-editor-header"
      >
        <Title level={5} className="m-0 truncate">
          {isEdit ? "Edit Collection Location" : "New Collection Location"}
        </Title>
        <Button
          type="text"
          aria-label="Close"
          icon={<CloseOutlined />}
          // Same path as Cancel, so closing behaves identically either way.
          onClick={editor.close}
        />
      </Flex>

      <div className="trapsite-editor-scroll">
        <Flex vertical gap="middle" className="trapsite-editor-body">
          <div className="flex flex-col gap-2">
            <Text type="secondary">Name</Text>
            <Input
              value={draft.displayName}
              placeholder="Collection location name"
              onChange={(e) =>
                editor.patchDraft({ displayName: e.target.value })
              }
            />
          </div>

          <div className="flex flex-col gap-2">
            <Text type="secondary">Description</Text>
            <TextArea
              value={draft.description}
              placeholder="Notes about this location"
              autoSize={{ minRows: 2, maxRows: 4 }}
              onChange={(e) =>
                editor.patchDraft({ description: e.target.value })
              }
            />
          </div>

          <div className="flex flex-col gap-2 min-w-0">
            <Text type="secondary">Radius</Text>
            <Space.Compact className="w-full">
              <InputNumber
                className="flex-1 trapsite-radius-input"
                size="large"
                // Spinners are useless at this scale - nobody nudges a radius
                // one unit at a time - and they eat width the number needs.
                controls={false}
                value={draft.radiusValue}
                min={0}
                // Stored exactly as typed, in the unit beside it. Nothing is
                // converted while typing, so digits are never rewritten
                // underneath the cursor.
                step={unitAllowsDecimals(draft.radiusUnit) ? 0.1 : 10}
                onChange={(value: number | null) =>
                  editor.patchDraft({
                    radiusValue: typeof value === "number" ? value : 0,
                  })
                }
              />
              <Select<DistanceUnit>
                size="large"
                className="trapsite-radius-unit"
                value={draft.radiusUnit}
                options={DISTANCE_UNITS.map((unit) => ({
                  value: unit,
                  label: unit,
                }))}
                // Changing the unit converts what is already there, which is
                // the one moment a conversion is what the user asked for.
                onChange={(unit) =>
                  editor.patchDraft({
                    radiusUnit: unit,
                    radiusValue: convertDistance(
                      draft.radiusValue,
                      draft.radiusUnit,
                      unit,
                    ),
                  })
                }
                getPopupContainer={() => container || document.body}
                styles={{ popup: { root: { zIndex: POPUP_Z_INDEX } } }}
              />
            </Space.Compact>
          </div>

          <AddressField
            address={draft.address}
            pin={draft.gps}
            geocodedGps={draft.geocodedGps}
            candidates={candidates}
            searching={searching}
            reverseLoading={reverseLoading}
            lookupError={lookupError}
            noMatch={noMatch}
            container={container}
            onAddressChange={(address) => {
              setNoMatch(false);
              setLookupError(null);
              editor.patchDraft({ address });
            }}
            onFind={handleFind}
            onUpdateFromPin={handleUpdateFromPin}
            onPickCandidate={handlePickCandidate}
            onSnapPinToAddress={() =>
              draft.geocodedGps && handleSnapPin(draft.geocodedGps)
            }
          />

          <Text type="secondary" className="text-xs">
            Drag any pin on the map to move it.
          </Text>

          <TrapsiteDeviceList
            devices={devices}
            selectedIds={draft.deviceIds}
            editingId={editor.editingId}
            onChange={handleDevicesChange}
            container={container}
          />

          {overlapping.length > 0 && (
            <Alert
              type="warning"
              showIcon
              message={
                overlapping.length === 1
                  ? `This location overlaps ${overlapping[0].name}.`
                  : `This location overlaps ${overlapping.length} others.`
              }
              description={
                <div className="max-w-72 flex flex-col gap-1">
                  {overlapping.map((site) => (
                    <Text key={site.id} className="text-xs">
                      {site.name} - centre {formatDistance(site.distance)} away
                      {site.clearAt > 0
                        ? `, clear of it below ${formatDistance(site.clearAt)}`
                        : ", and this pin sits inside it"}
                    </Text>
                  ))}
                  <Text type="secondary" className="text-xs">
                    {/* Said here as well as drawn on the map: the editor opens
                        zoomed in on the pin, so the shared ground can be
                        off-screen entirely. */}
                    A trap in the shared ground could belong to either location.
                    Shrink the radius or move a pin to separate them.
                  </Text>
                </div>
              }
            />
          )}

          {farTraps.length > 0 && (
            <Alert
              type="warning"
              showIcon
              message={
                farTraps.length === 1
                  ? `${farTraps[0].name} is ${formatDistance(farTraps[0].distance)} from this location.`
                  : `${farTraps.length} traps are far from this location.`
              }
              description={
                <div className="max-w-72 flex flex-col gap-1">
                  {farTraps.length > 1 &&
                    farTraps.map((trap) => (
                      <Text key={trap.uuid} className="text-xs">
                        {trap.name} - {formatDistance(trap.distance)}
                      </Text>
                    ))}
                  <Text type="secondary" className="text-xs">
                    Drag its pin to where the trap actually is, or leave it if
                    the location covers that ground.
                  </Text>
                </div>
              }
            />
          )}

          {saveError && (
            <Alert
              type="error"
              showIcon
              message={saveError}
              closable
              onClose={() => setSaveError(null)}
            />
          )}

          <Flex justify="space-between" align="center" className="mt-2">
            {isEdit ? (
              <Popconfirm
                title="Delete this collection location?"
                // Only says anything when there is a consequence to warn
                // about; an empty location just asks the question.
                description={
                  draft.deviceIds.length > 0 ? (
                    <div className="max-w-64">
                      {draft.deviceIds.length === 1
                        ? "1 device will become Unassigned."
                        : `${draft.deviceIds.length} devices will become Unassigned.`}
                    </div>
                  ) : undefined
                }
                okText="Delete"
                okButtonProps={{ danger: true }}
                cancelText="Cancel"
                getPopupContainer={() => container || document.body}
                zIndex={POPUP_Z_INDEX}
                onConfirm={handleDelete}
              >
                <Button danger type="text" loading={deleting}>
                  Delete
                </Button>
              </Popconfirm>
            ) : (
              <span />
            )}

            <Space>
              <Button onClick={editor.close}>Cancel</Button>
              <Popconfirm
                // Only stands in the way when assignments actually move.
                disabled={!deviceListChanged || isEmptyDiff(diff)}
                title="Change device assignments?"
                description={deviceSummary}
                okText="Confirm"
                cancelText="Cancel"
                getPopupContainer={() => container || document.body}
                zIndex={POPUP_Z_INDEX}
                onConfirm={submit}
              >
                <Button
                  type="primary"
                  loading={saving}
                  disabled={!canSave}
                  onClick={() => {
                    // Popconfirm does not swallow its child's onClick, so
                    // without this guard a device change would open the confirm
                    // AND save at the same time.
                    if (!deviceListChanged || isEmptyDiff(diff)) submit();
                  }}
                >
                  Save
                </Button>
              </Popconfirm>
            </Space>
          </Flex>
        </Flex>
      </div>
    </div>
  );
};

export default TrapsiteEditorPanel;
